import { useEffect, useRef, useState } from 'react';
import {
 Alert, AlertIcon, AlertTitle, AlertDescription, AlertDialog, AlertDialogOverlay,
 AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
 Box, Button, Flex, Heading, HStack, Icon, Spinner, Text, Textarea,
 FormControl, FormLabel, FormErrorMessage, usePrefersReducedMotion,
} from '@chakra-ui/react';
import { RiMegaphoneLine, RiErrorWarningLine, RiCheckLine } from 'react-icons/ri';
import { useCampaignUI, primaryButton, dangerButton } from './ui';
import { statusMeta } from './status';
import { CampaignSurface } from './CampaignLayout';

export function CampaignCard({ children, ...props }) {
 const ui = useCampaignUI();
 return <Box bg={ui.surface} border='1px solid' borderColor={ui.borderSubtle} borderRadius='xl'
  boxShadow={ui.shadowCard} p={5} minW={0} {...props}>{children}</Box>;
}
/** Campaign-specific structure using the app's original typography and surfaces. */
export function CampaignPage({ title, subtitle, icon = RiMegaphoneLine, actions, children }) {
 const ui = useCampaignUI();
 return <CampaignSurface>
  <Flex justify='space-between' align={{ base: 'flex-start', md: 'center' }} wrap='wrap' gap={4} mb={5}>
   <HStack spacing={3} align='flex-start' flex='1 1 260px' minW={0}>
    <Icon as={icon} color={ui.accentInk} boxSize={6} mt={0.5} flexShrink={0} aria-hidden='true' />
    <Box minW={0}>
     <Heading as='h1' size='md' color={ui.textPrimary} overflowWrap='anywhere'>{title}</Heading>
     {subtitle && <Text color={ui.textSecondary} fontSize='sm' mt={1} maxW='78ch'>{subtitle}</Text>}
    </Box>
   </HStack>
   {actions && <Flex gap={2} wrap='wrap' flexShrink={0}>{actions}</Flex>}
  </Flex>
  <Box minW={0}>{children}</Box>
 </CampaignSurface>;
}
export function StatusBadge({ status, context = 'generic', label, ...props }) {
 const ui = useCampaignUI(); const reduced = usePrefersReducedMotion();
 const meta = statusMeta(status, context); const tone = ui.tones[meta.tone] ?? ui.tones.neutral;
 return <HStack as='span' display='inline-flex' spacing={1.5} px={2.5} py={1} borderRadius='full'
  border='1px solid' borderColor={tone.border} bg={tone.bg} color={tone.fg}
  fontSize='xs' fontWeight='semibold' lineHeight='1.4' whiteSpace='nowrap' {...props}>
  {status === 'PLANNING' && !reduced ? <Spinner size='xs' speed='0.9s' aria-hidden='true' />
   : status === 'COMPLETED' ? <Icon as={RiCheckLine} boxSize={3} aria-hidden='true' />
    : <Box as='span' boxSize='6px' borderRadius='full' bg='currentColor' aria-hidden='true' />}
  <Text as='span'>{label ?? meta.label}</Text>
 </HStack>;
}
export function SectionCard({ title, subtitle, actions, children, p = 5, ...props }) {
 const ui = useCampaignUI();
 return <CampaignCard p={0} {...props}>
  {(title || actions) && <Flex justify='space-between' align='flex-start' gap={3} px={5} pt={5} pb={3} wrap='wrap'>
   <Box><Heading as='h2' size='sm' color={ui.textPrimary}>{title}</Heading>
    {subtitle && <Text fontSize='sm' color={ui.textMuted} mt={1}>{subtitle}</Text>}</Box>{actions}
  </Flex>}
  <Box p={p} pt={p === 0 ? 0 : title ? 2 : p}>{children}</Box>
 </CampaignCard>;
}
export function StatCard({ label, value, help, icon, tone = 'neutral', ...props }) {
 const ui = useCampaignUI(); const palette = ui.tones[tone] ?? ui.tones.neutral;
 return <CampaignCard p={{ base: 3, md: 4 }} {...props}>
  <Flex justify='space-between' align='center' gap={2}>
   <Text fontSize='xs' color={ui.textSecondary} fontWeight='semibold'>{label}</Text>
   {icon && <Flex boxSize={8} align='center' justify='center' borderRadius='lg' bg={palette.bg} color={palette.fg}><Icon as={icon} aria-hidden='true' /></Flex>}
  </Flex>
  <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight='700' color={ui.textPrimary} mt={1} overflowWrap='anywhere'>{value}</Text>
  {help && <Text fontSize='xs' color={ui.textMuted} mt={1}>{help}</Text>}
 </CampaignCard>;
}
export function Banner({ title, description, status = 'info', children, ...props }) {
 const ui = useCampaignUI(); const tone = ui.tones[{ error: 'red', warning: 'amber', success: 'green', info: 'blue' }[status] ?? 'blue'];
 return <Alert status={status} alignItems='flex-start' borderRadius='xl' border='1px solid' borderColor={tone.border}
  bg={tone.bg} color={tone.fg} {...props}>
  <AlertIcon color={tone.fg} mt={0.5} />
  <Box minW={0}>{title && <AlertTitle fontSize='sm' mb={1}>{title}</AlertTitle>}
   {description && <AlertDescription display='block' fontSize='sm' overflowWrap='anywhere'>{description}</AlertDescription>}{children}
  </Box>
 </Alert>;
}
export function EmptyState({ icon = RiMegaphoneLine, title = 'Nothing here yet', description, action, children }) {
 const ui = useCampaignUI();
 return <Flex direction='column' align='center' textAlign='center' px={5} py={{ base: 8, md: 12 }}>
  <Flex align='center' justify='center' boxSize='48px' mb={4} borderRadius='xl' bg={ui.accentSubtle} color={ui.textPrimary}>
   <Icon as={icon} boxSize={7} aria-hidden='true' />
  </Flex>
  <Heading as='h3' size='sm' color={ui.textPrimary}>{title}</Heading>
  {description && <Text maxW='54ch' fontSize='sm' color={ui.textSecondary} mt={2} lineHeight='1.7'>{description}</Text>}
  {action && <Box mt={5}>{action}</Box>}{children}
 </Flex>;
}
export function ErrorState({ title = 'Something went wrong', description, onRetry }) {
 const ui = useCampaignUI();
 return <EmptyState icon={RiErrorWarningLine} title={title} description={description}
  action={onRetry && <Button onClick={onRetry} size='sm' {...primaryButton(ui)}>Try again</Button>} />;
}
/** Focus starts on the safe action. Rejection errors keep the dialog and reason open. */
export function ConfirmDialog({ isOpen, onClose, title, description, confirmLabel = 'Confirm', confirmScheme = 'red',
 isLoading = false, requireReason = false, showReason = false, reasonLabel = 'Reason', reasonPlaceholder, onConfirm, children }) {
 const ui = useCampaignUI(); const cancelRef = useRef(); const [reason, setReason] = useState('');
 const [localBusy, setLocalBusy] = useState(false); const [error, setError] = useState('');
 const submitting = useRef(false); const busy = isLoading || localBusy;
 useEffect(() => { if (isOpen) { setReason(''); setError(''); } }, [isOpen]);
 const confirm = async () => {
  if (submitting.current || busy || (requireReason && !reason.trim())) return;
  submitting.current = true; setLocalBusy(true); setError('');
  try { const ok = await onConfirm?.(reason.trim()); if (ok !== false) onClose?.(); }
  catch (err) { setError(typeof err?.message === 'string' ? err.message : 'The action failed. Check the campaign status and try again.'); }
  finally { submitting.current = false; setLocalBusy(false); }
 };
 return <AlertDialog isOpen={isOpen} onClose={busy ? () => {} : onClose} leastDestructiveRef={cancelRef}
  isCentered closeOnOverlayClick={!busy} closeOnEsc={!busy} size='lg'>
  <AlertDialogOverlay><AlertDialogContent bg={ui.surface} color={ui.textPrimary} mx={3}>
   <AlertDialogHeader fontSize='lg'>{title}</AlertDialogHeader>
   <AlertDialogBody>
    <Text fontSize='sm' color={ui.textSecondary} lineHeight='1.7'>{description}</Text>
    {children && <Box mt={4}>{children}</Box>}
    {(requireReason || showReason) && <FormControl mt={4} isRequired={requireReason} isInvalid={Boolean(error)}>
     <FormLabel fontSize='sm'>{reasonLabel}</FormLabel>
     <Textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder={reasonPlaceholder}
      bg={ui.inputBg} borderColor={ui.border} isDisabled={busy} maxLength={1000} />
     <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>}
    {error && !(requireReason || showReason) && <Text role='alert' color={ui.danger} fontSize='sm' mt={3}>{error}</Text>}
   </AlertDialogBody>
   <AlertDialogFooter gap={2} flexWrap='wrap'>
    <Button ref={cancelRef} onClick={onClose} variant='neutralOutline' borderColor={ui.border} color={ui.textPrimary} isDisabled={busy}>Go back</Button>
    <Button onClick={confirm} isLoading={busy} loadingText='Working...' isDisabled={requireReason && !reason.trim()}
     {...(confirmScheme === 'red' ? dangerButton(ui) : primaryButton(ui))}>{confirmLabel}</Button>
   </AlertDialogFooter>
  </AlertDialogContent></AlertDialogOverlay>
 </AlertDialog>;
}
